import React, { useCallback, useEffect, useMemo, useState } from "react";
import type { Result } from "@zxing/library";
import { useZxingScanner } from "../../hooks/useZxingScanner";
import styles from "./QRBarcodeScanner.module.css";
import { IonSpinner } from "@ionic/react";

export type QRBarcodeScannerPayload = {
  text: string;
  format: string;
};

type QRBarcodeScannerProps = {
  active: boolean;
  onDetected: (payload: QRBarcodeScannerPayload) => void;
  className?: string;
};

const QRBarcodeScanner: React.FC<QRBarcodeScannerProps> = ({
  active,
  onDetected,
  className,
}) => {
  const [videoElement, setVideoElement] = useState<HTMLVideoElement | null>(
    null
  );

  const handleVideoRef = useCallback((node: HTMLVideoElement | null) => {
    setVideoElement(node);
  }, []);

  const handleResult = useCallback(
    (result: Result) => {
      onDetected({
        text: result.getText(),
        format: result.getBarcodeFormat().toString(),
      });
    },
    [onDetected]
  );

  const { isReady, stop } = useZxingScanner(
    videoElement,
    handleResult,
    active
  );

  useEffect(() => {
    return () => {
      stop();
    };
  }, [stop]);

  const containerClass = useMemo(
    () => (className ? `${className} ${styles.container}` : styles.container),
    [className]
  );

  return (
    <div className={containerClass}>
      <video
        ref={handleVideoRef}
        playsInline
        muted
        autoPlay
        className={styles.video}
        controls={false}
        preload="none"
        disablePictureInPicture
        poster="data:image/gif;base64,R0lGODlhAQABAAD/ACwAAAAAAQABAAACADs="
      />
      <div className={styles.overlay} aria-hidden>
        <div className={styles.brackets}>
          <span className={`${styles.bracket} ${styles.topLeft}`} />
          <span className={`${styles.bracket} ${styles.topRight}`} />
          <span className={`${styles.bracket} ${styles.bottomLeft}`} />
          <span className={`${styles.bracket} ${styles.bottomRight}`} />
        </div>
      </div>
      {!isReady && active && (
        <div className={styles.loadingOverlay}><IonSpinner name="crescent" /></div>
      )}
    </div>
  );
};

export default QRBarcodeScanner;



